import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

import { UserCheck, Users, Clock, RefreshCw, History, DollarSign, Plus, Eye, CheckCircle, XCircle, Download } from 'lucide-react';

export default function AdhesionsTabs({ ctx }) {
  const { adhesions, tarifs, activeTab, setActiveTab, setOpenCreateAdhesion, setOpenCreateTarif, setOpenDetailAdhesion, setOpenValidation, setOpenGenererDoc, setSelectedAdhesion, setValidationData, filteredAdhesions, handleRenouveler } = ctx;
  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              <TabsTrigger value="overview">
                <Users className="h-4 w-4 mr-2" />
                Vue d'ensemble
              </TabsTrigger>
              <TabsTrigger value="actives">
                <CheckCircle className="h-4 w-4 mr-2" />
                Actives
              </TabsTrigger>
              <TabsTrigger value="pending">
                <Clock className="h-4 w-4 mr-2" />
                En attente
              </TabsTrigger>
              <TabsTrigger value="renew">
                <RefreshCw className="h-4 w-4 mr-2" />
                À renouveler
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-2" />
                Historique
              </TabsTrigger>
              <TabsTrigger value="tarifs">
                <DollarSign className="h-4 w-4 mr-2" />
                Tarifs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Dernières adhésions</h3>
                {adhesions.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <UserCheck className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucune adhésion pour le moment</p>
                    <Button className="mt-4" onClick={() => setOpenCreateAdhesion(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Créer la première adhésion
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAdhesions.slice(0, 10).map((adhesion) => (
                      <Card key={adhesion.id} className="hover:shadow-lg transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-full ${
                                adhesion.source === 'ftpe' ? 'bg-blue-100' :
                                adhesion.source === 'rcd' ? 'bg-blue-100' :
                                'bg-purple-100'
                              }`}>
                                <UserCheck className={`h-6 w-6 ${
                                  adhesion.source === 'ftpe' ? 'text-blue-600' :
                                  adhesion.source === 'rcd' ? 'text-blue-600' :
                                  'text-purple-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-semibold">{adhesion.membre_nom}</p>
                                <p className="text-sm text-slate-600">{adhesion.numero_adhesion || adhesion.id?.substring(0, 8)}</p>
                                <Badge className={`text-xs ${
                                  adhesion.source === 'ftpe' ? 'bg-blue-600' :
                                  adhesion.source === 'rcd' ? 'bg-blue-600' :
                                  'bg-purple-600'
                                }`}>
                                  {adhesion.type_source || 'Adhésion'}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={
                                adhesion.statut === 'active' || adhesion.statut === 'validee' ? 'bg-green-700' :
                                adhesion.statut === 'en_attente' || adhesion.statut === 'pending' || adhesion.statut === 'en_cours' ? 'bg-orange-600' :
                                adhesion.statut === 'expiree' || adhesion.statut === 'expired' ? 'bg-red-600' :
                                'bg-slate-700'
                              }>
                                {adhesion.statut}
                              </Badge>
                              <p className="text-lg font-bold">{adhesion.montant || 0} €</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="actives" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Adhésions actives ({filteredAdhesions.filter(a => ['active', 'validee', 'completed', 'paid'].includes(a.statut)).length})</h3>
                
                {filteredAdhesions.filter(a => ['active', 'validee', 'completed', 'paid'].includes(a.statut)).length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <CheckCircle className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucune adhésion active</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAdhesions.filter(a => ['active', 'validee', 'completed', 'paid'].includes(a.statut)).slice(0, 20).map((adhesion) => (
                      <Card key={adhesion.id} className="border-2 border-green-100 hover:shadow-lg transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-green-100 rounded-full">
                                <UserCheck className="h-6 w-6 text-green-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-lg">{adhesion.membre_nom}</p>
                                <p className="text-sm text-slate-600">N°: {adhesion.numero_adhesion}</p>
                                <p className="text-xs text-slate-500">{adhesion.membre_email}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4 md:gap-6">
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Type</p>
                                <Badge className="bg-blue-700 text-white font-semibold">{adhesion.type_adhesion}</Badge>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Montant</p>
                                <p className="text-xl font-bold text-green-600">{adhesion.montant} €</p>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Expire le</p>
                                <p className="text-sm font-semibold">
                                  {new Date(adhesion.date_fin).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                              
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAdhesion(adhesion);
                                    setOpenDetailAdhesion(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                
                                <Button 
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleRenouveler(adhesion.id)}
                                >
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Renouveler
                                </Button>
                                
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAdhesion(adhesion);
                                    setOpenGenererDoc(true);
                                  }}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pending" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Demandes en attente ({filteredAdhesions.filter(a => ['en_attente', 'pending', 'en_cours', 'created'].includes(a.statut)).length})</h3>
                
                {filteredAdhesions.filter(a => ['en_attente', 'pending', 'en_cours', 'created'].includes(a.statut)).length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Clock className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucune demande en attente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAdhesions.filter(a => ['en_attente', 'pending', 'en_cours', 'created'].includes(a.statut)).slice(0, 20).map((adhesion) => (
                      <Card key={adhesion.id} className="border-2 border-orange-100 hover:shadow-lg transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-orange-100 rounded-full">
                                <Clock className="h-6 w-6 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-lg">{adhesion.membre_nom}</p>
                                <p className="text-sm text-slate-600">N°: {adhesion.numero_adhesion}</p>
                                <p className="text-xs text-slate-500">
                                  Demande du {new Date(adhesion.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4 md:gap-6">
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Type</p>
                                <Badge className="bg-orange-600 text-white font-semibold">{adhesion.type_adhesion}</Badge>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Montant</p>
                                <p className="text-xl font-bold">{adhesion.montant} €</p>
                              </div>
                              
                              <div className="flex gap-2">
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    setSelectedAdhesion(adhesion);
                                    setValidationData({ action: 'valider', raison: '' });
                                    setOpenValidation(true);
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Valider
                                </Button>
                                
                                <Button 
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedAdhesion(adhesion);
                                    setValidationData({ action: 'rejeter', raison: '' });
                                    setOpenValidation(true);
                                  }}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Rejeter
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="renew" className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Adhésions à renouveler (expire dans 30 jours)</h3>
                  <Badge className="bg-blue-700 text-white text-lg px-3 py-1 font-semibold">
                    {filteredAdhesions.filter(a => {
                      if (!['active', 'validee'].includes(a.statut)) return false;
                      const dateFin = new Date(a.date_fin);
                      const dans30Jours = new Date();
                      dans30Jours.setDate(dans30Jours.getDate() + 30);
                      return dateFin <= dans30Jours;
                    }).length}
                  </Badge>
                </div>
                
                {filteredAdhesions.filter(a => {
                  if (!['active', 'validee'].includes(a.statut)) return false;
                  const dateFin = new Date(a.date_fin);
                  const dans30Jours = new Date();
                  dans30Jours.setDate(dans30Jours.getDate() + 30);
                  return dateFin <= dans30Jours;
                }).length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <RefreshCw className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucune adhésion à renouveler prochainement</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAdhesions.filter(a => {
                      if (!['active', 'validee'].includes(a.statut)) return false;
                      const dateFin = new Date(a.date_fin);
                      const dans30Jours = new Date();
                      dans30Jours.setDate(dans30Jours.getDate() + 30);
                      return dateFin <= dans30Jours;
                    }).slice(0, 20).map((adhesion) => {
                      const joursRestants = Math.ceil((new Date(adhesion.date_fin) - new Date()) / (1000 * 60 * 60 * 24));
                      const urgence = joursRestants <= 7 ? 'bg-red-100 border-red-200' : joursRestants <= 15 ? 'bg-orange-100 border-orange-200' : 'bg-blue-100 border-blue-200';
                      
                      return (
                        <Card key={adhesion.id} className={`border-2 ${urgence} hover:shadow-lg transition-shadow`}>
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-full ${urgence}`}>
                                  <RefreshCw className={`h-6 w-6 ${joursRestants <= 7 ? 'text-red-600' : joursRestants <= 15 ? 'text-orange-600' : 'text-blue-600'}`} />
                                </div>
                                <div>
                                  <p className="font-semibold text-lg">{adhesion.membre_nom}</p>
                                  <p className="text-sm text-slate-600">N°: {adhesion.numero_adhesion}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge className={joursRestants <= 7 ? 'bg-red-600 text-white' : joursRestants <= 15 ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'}>
                                      {joursRestants > 0 ? `${joursRestants} jour(s) restant(s)` : 'Expiré'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4 md:gap-6">
                                <div className="text-right">
                                  <p className="text-sm text-slate-600">Expire le</p>
                                  <p className="text-lg font-bold text-red-600">
                                    {new Date(adhesion.date_fin).toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                                
                                <div className="text-right">
                                  <p className="text-sm text-slate-600">Montant</p>
                                  <p className="text-xl font-bold">{adhesion.montant} €</p>
                                </div>
                                
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleRenouveler(adhesion)}
                                >
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Renouveler maintenant
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Historique complet ({adhesions.length})</h3>
                
                {adhesions.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <History className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucun historique</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {adhesions.map((adhesion) => (
                      <Card key={adhesion.id} className="hover:shadow-lg transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="p-2 bg-slate-100 rounded-full">
                                <History className="h-5 w-5 text-slate-600" />
                              </div>
                              <div>
                                <p className="font-semibold">{adhesion.membre_nom}</p>
                                <p className="text-xs text-slate-500">
                                  {adhesion.numero_adhesion} • {adhesion.type_adhesion}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-xs text-slate-600">
                                Du {new Date(adhesion.date_debut).toLocaleDateString('fr-FR')} au {new Date(adhesion.date_fin).toLocaleDateString('fr-FR')}
                              </div>
                              
                              <Badge className={
                                adhesion.statut === 'active' ? 'bg-green-700' :
                                adhesion.statut === 'en_attente' ? 'bg-orange-600' :
                                adhesion.statut === 'expiree' ? 'bg-red-600' :
                                adhesion.statut === 'rejetee' ? 'bg-red-800' :
                                'bg-slate-700'
                              }>
                                {adhesion.statut}
                              </Badge>
                              
                              <p className="text-sm font-bold">{adhesion.montant} €</p>
                              
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedAdhesion(adhesion);
                                  setOpenDetailAdhesion(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="tarifs" className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Tarifs d'adhésion</h3>
                  <Button onClick={() => setOpenCreateTarif(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau tarif
                  </Button>
                </div>
                
                {tarifs.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <DollarSign className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Aucun tarif configuré</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tarifs.map((tarif) => (
                      <Card key={tarif.id} className="border-2">
                        <CardHeader>
                          <CardTitle>{tarif.nom}</CardTitle>
                          <CardDescription>{tarif.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="text-3xl font-bold text-blue-600">
                              {tarif.montant_annuel} €
                              <span className="text-sm font-normal text-slate-600">/an</span>
                            </div>
                            {tarif.actif ? (
                              <Badge className="bg-green-600 text-white font-semibold">Actif</Badge>
                            ) : (
                              <Badge className="bg-slate-600 text-white font-semibold">Inactif</Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
